import { useState } from "react";
import { BookOpen, ChevronDown, ChevronRight } from "lucide-react";
import type { ConditionGuidance } from "../../types";
import { getApi } from "../../api";
import { useAsyncResource } from "../../hooks/useAsyncResource";
import { ErrorState, Skeleton } from "../ScreenStates";
import { MarkdownText } from "../MarkdownText";
import { TagPill } from "../Pills";

interface GuidancePanelProps {
  conditionId: number;
  children?: never;
}

function RulesBody({ guidance }: { guidance: ConditionGuidance }) {
  if (!guidance.underwritingRule && !guidance.skynetRule) return <p className="text-sm text-gray-600">No underwriting rules recorded for this condition. Actuarial can add them on Customize Conditions.</p>;

  return (
    <div className="space-y-4">
      {guidance.underwritingRule && (
        <div>
          <TagPill label="Underwriting Rule" tone="green" />
          <div className="mt-1">
            <MarkdownText text={guidance.underwritingRule} />
          </div>
        </div>
      )}
      {guidance.skynetRule && (
        <div>
          <TagPill label="Condition-Specific Rule (Skynet)" tone="navy" />
          <p className="mt-1 text-sm text-gray-700">{guidance.skynetRule}</p>
          <p className="mt-1 text-xs text-gray-500">Maintained in Skynet Replay; shown here read-only.</p>
        </div>
      )}
    </div>
  );
}

/**
 * Collapsed strip above the ratings table holding the underwriting rule (Markdown written by the
 * underwriting team on Customize Conditions) and the Condition-Specific Rule Skynet follows. Opens on
 * demand so reference text never takes space from the picker.
 */
export function GuidancePanel({ conditionId }: GuidancePanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const guidance = useAsyncResource(() => getApi().getConditionGuidance(conditionId), String(conditionId), "GuidancePanel.load");
  const { state } = guidance;
  const ruleCount = (state.data?.underwritingRule ? 1 : 0) + (state.data?.skynetRule ? 1 : 0);

  return (
    <section aria-label="Underwriting rules" className="rounded-2xl bg-white shadow-md">
      <button type="button" onClick={() => setIsOpen((current) => !current)} aria-expanded={isOpen} aria-controls={`rules-${conditionId}`} className="flex w-full cursor-pointer items-center justify-between rounded-2xl px-5 py-3 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-green-50">
        <span className="flex items-center gap-2 text-[15px] font-semibold text-brand-navy">
          <BookOpen size={18} aria-hidden="true" />
          Underwriting rules
          {state.data && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{ruleCount === 0 ? "none" : ruleCount}</span>}
        </span>
        <span className="flex items-center gap-1 text-sm text-gray-500">
          {isOpen ? "Hide" : "Show"}
          {isOpen ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
        </span>
      </button>
      {isOpen && (
        <div id={`rules-${conditionId}`} className="border-t border-gray-100 px-5 py-4">
          {state.kind === "error" && <ErrorState message={state.message} onRetry={guidance.reload} />}
          {state.kind === "loading" && !state.data && (
            <div className="space-y-2" role="status" aria-label="Loading rules">
              <Skeleton className="h-4 w-24" />
              <Skeleton />
            </div>
          )}
          {state.data && <RulesBody guidance={state.data} />}
        </div>
      )}
    </section>
  );
}
